import { useState, useRef, useEffect } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import './Form.css';
import { TextCss } from '../../components/GlobalCss/TextCss';

interface DescriptionTextareaProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  requiredMessage?: string;
  disabled?: boolean;
  validation?: {
    required?: string | boolean;
    minLength?: { value: number; message: string };
    maxLength?: { value: number; message: string };
    validate?: (value: any) => boolean | string;
    [key: string]: any;
  };
  className?: string;
  rows?: number;
  // Limit callbacks
  onError?: (msg: string) => void;
  onClearError?: () => void;
}

const DescriptionTextarea: React.FC<DescriptionTextareaProps> = ({
  label = 'Description',
  name,
  required = false,
  placeholder = 'Enter description...',
  requiredMessage = 'This field is required',
  disabled = false,
  validation = {},
  className = '',
  rows = 5,
  onError,
  onClearError,
  ...rest
}) => {
  const { control, formState: { isSubmitted }, trigger } = useFormContext();
  const [limitError, setLimitError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    field: { onChange, value, onBlur, ref },
    fieldState: { error, isTouched },
  } = useController({
    name,
    control,
    rules: {
      required: required ? requiredMessage : false,
      ...validation,
    },
  });

  const maxLengthRule = validation?.maxLength;
  const maxLength = (maxLengthRule as any)?.value || maxLengthRule;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;

    if (maxLength && val.length > maxLength) {
      const msg = `${label || "Field"} cannot exceed ${maxLength} characters`;
      
      // Clear any existing timeout
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      
      setLimitError(msg);
      if (onError) {
        onError(msg);
      }

      // Hide error after 1 second
      timeoutRef.current = setTimeout(() => {
        setLimitError(null);
        if (onClearError) onClearError();
      }, 1000);

      return; // Block input
    }

    // If typing normally below limit, clear errors immediately
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLimitError(null);
    if (onClearError) {
      onClearError();
    }
    onChange(val);

    // Must run unconditionally, not just "if (error)" -- react-hook-form's
    // formState.isValid lags a full render behind field.onChange under
    // mode: "onChange" (a known RHF quirk), so a Save button gated on
    // isValid stays disabled for the keystroke that actually made the form
    // valid, only catching up on the *next* interaction. Calling trigger()
    // here forces isValid to resolve in the same interaction.
    trigger(name);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const showErrorMessage = limitError || (((isTouched || isSubmitted) && error) ? (error?.message as string | undefined) : undefined);

  // Validate on blur explicitly rather than relying on the parent form's
  // `mode` (most forms use the RHF default "onSubmit", which never
  // populates `fieldState.error` until a submit attempt — and Save stays
  // disabled while the form is invalid, so that submit can never happen).
  const handleBlur = () => {
    onBlur();
    trigger(name);
  };

  return (
    <div className={`form-group ${className}`}>
      {label &&
        <label className={TextCss.black.sub_heading_600_14}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      }
      <textarea
        disabled={disabled}
        rows={rows}
        className={`form-control ${showErrorMessage ? 'is-invalid' : ''}`}
        placeholder={placeholder}
        value={value || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={ref}
        {...rest}
      />
      {maxLength && (
        <div className="d-flex justify-content-end mt-n2 mb-2">
          <span className="sub_text grey_subtext1" style={{ fontSize: '12px', marginTop: '4px' }}>
            {String(value || "").length}/{maxLength}
          </span>
        </div>
      )}
      {showErrorMessage && (
        <div className="invalid-feedback">
          {showErrorMessage}
        </div>
      )}
    </div>
  );
};

export default DescriptionTextarea;
